import React, { useState } from 'react';
import { Check, Minus } from 'lucide-react';
import { useT, useLang, itemHasLocale } from '../lib/i18n';

// ─────────────────────────────────────────────────────────────────────────
// ModernPricing — the «Moderno» theme Pricing (s139, approved by Anthony:
// R1 "Three Columns, featured dark"). One column per plan; the is_featured plan
// is graphite and slightly raised with its badge (first on phones). Monthly /
// annual pill (when show_toggle) with the saving computed from the prices.
// Features one per line; "✗" / "x " prefix = excluded (struck, grey).
// Data = aurex_pricing (config + items name/price/price_annual/currency/period/
// description/badge/is_featured/cta_*/features). Colours from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';

function parseFeatures(raw) {
  return String(raw || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => ({
    included: !l.startsWith('✗') && !l.toLowerCase().startsWith('x '),
    text: l.replace(/^✗\s*/, '').replace(/^x\s+/i, '').trim(),
  }));
}
const num = (v) => parseFloat(String(v || '').replace(/[^0-9.]/g, ''));

function Plan({ plan, annual }) {
  const tt = useT();
  const featured = !!plan.is_featured;
  const price = annual ? (plan.price_annual || plan.price) : plan.price;
  const features = parseFeatures(tt(plan.features));
  const ink = featured ? '#f4f1ea' : 'var(--color-heading, #1a2332)';
  const muted = featured ? 'rgba(244,241,234,.6)' : 'var(--color-body-text, #64748b)';
  const ctaText = tt(plan.cta_text);
  return (
    <div className={`flex flex-col gap-5 p-7 lg:p-8 ${featured ? 'order-first md:order-none md:-mt-4 md:pt-10' : ''}`}
      style={{ background: featured ? GRAPHITE : '#fff', border: `1px solid ${featured ? GRAPHITE : 'rgba(20,26,58,.1)'}` }} data-testid={`pricing-plan-${plan.id}`}>
      {tt(plan.badge) && (
        <span className="self-start text-[10px] font-bold uppercase px-2.5 py-1 rounded-full" style={{ letterSpacing: '.12em', background: featured ? 'var(--color-accent, #ff9c1b)' : 'color-mix(in srgb, var(--color-accent, #ff9c1b) 14%, #fff)', color: featured ? 'var(--accent-ink, #0f172a)' : 'var(--accent-strong, #b86500)' }}>{tt(plan.badge)}</span>
      )}
      <div>
        <div className="font-bold text-xl" style={{ fontFamily: SERIF, color: ink }}>{tt(plan.name)}</div>
        {tt(plan.description) && <p className="mt-1 text-sm" style={{ color: muted }}>{tt(plan.description)}</p>}
      </div>
      <div className="flex items-baseline gap-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <span className="font-bold" style={{ fontFamily: SERIF, fontSize: '1.4rem', color: ink }}>{tt(plan.currency) || '$'}</span>
        <span className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2.4rem, 3.4vw, 3rem)', lineHeight: 1, color: ink }}>{tt(price)}</span>
        {tt(plan.period) && <span className="text-sm ml-1" style={{ color: muted }}>{tt(plan.period)}</span>}
      </div>
      {features.length > 0 && (
        <ul className="flex flex-col gap-3 pt-5 border-t" style={{ borderColor: featured ? 'rgba(255,255,255,.12)' : 'rgba(20,26,58,.08)' }}>
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-sm leading-snug" style={{ color: f.included ? ink : muted }}>
              {f.included
                ? <Check className="w-4 h-4 flex-none mt-0.5" strokeWidth={2.6} style={{ color: featured ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }} />
                : <Minus className="w-4 h-4 flex-none mt-0.5" style={{ opacity: 0.5 }} />}
              <span style={f.included ? undefined : { textDecoration: 'line-through', opacity: 0.7 }}>{f.text}</span>
            </li>
          ))}
        </ul>
      )}
      {ctaText && (
        <a href={tt(plan.cta_url) || '#'} target={plan.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" data-testid={`pricing-cta-${plan.id}`}
          className={`mt-auto text-center font-semibold text-sm px-6 py-3.5 rounded-[2px] mx-btn ${featured ? 'mx-btn--primary mx-dark' : 'mx-btn--secondary'}`}
          style={featured
            ? { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }
            : { border: '1.5px solid var(--color-heading, #1a2332)', color: 'var(--color-heading, #1a2332)' }}>
          {ctaText}
        </a>
      )}
    </div>
  );
}

export default function ModernPricing({ config = {}, items = [] }) {
  const tt = useT();
  const { lang } = useLang();
  const [annual, setAnnual] = useState(false);
  const plans = (items || []).filter(p => p.visible !== false && itemHasLocale(p.name, lang));
  if (!plans.length) return null;

  // Saving shown on the "Annual" pill: from the first plan with both prices.
  const ref = plans.find(p => num(p.price) > 0 && num(p.price_annual) > 0);
  const saving = ref ? Math.round((1 - num(ref.price_annual) / num(ref.price)) * 100) : 0;

  const title = tt(config.title);
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const last = words.length > 1 ? words.pop() : null;
  const cols = plans.length >= 3 ? 'md:grid-cols-3' : plans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'max-w-md mx-auto';

  return (
    <section id="aurex-pricing" data-testid="aurex-section-pricing" className="bg-white">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col items-center text-center gap-4 mb-10">
          {tt(config.eyebrow) && (
            <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
              <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(config.eyebrow)}<span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />
            </div>
          )}
          {title && (
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="pricing-title">
              {words.join(' ')}{last && <> <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>}
            </h2>
          )}
          {tt(config.subtitle) && <p className="text-base md:text-lg leading-relaxed max-w-2xl" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(config.subtitle)}</p>}
          {config.show_toggle && (
            <div className="inline-flex rounded-full p-1 mt-3" style={{ background: '#efebe4' }} role="group" aria-label="Billing period" data-testid="pricing-toggle">
              {[false, true].map(isAnnual => (
                <button key={String(isAnnual)} type="button" onClick={() => setAnnual(isAnnual)} aria-pressed={annual === isAnnual}
                  className="px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200"
                  style={annual === isAnnual ? { background: '#fff', color: 'var(--color-heading, #1a2332)', boxShadow: '0 1px 4px rgba(0,0,0,.08)' } : { color: 'var(--color-body-text, #64748b)' }}>
                  {isAnnual ? 'Annual' : 'Monthly'}
                  {isAnnual && saving > 0 && <span className="ml-1.5 text-xs" style={{ color: 'var(--accent-strong, #b86500)' }}>−{saving}%</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`grid grid-cols-1 ${cols} gap-5 items-start pt-4`}>
          {plans.map(p => <Plan key={p.id} plan={p} annual={annual} />)}
        </div>
      </div>
    </section>
  );
}
